import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import {
  LoaderService,
  MD_MODAL_PARAMS,
  ToastsService,
  UPUtilityService,
} from 'up';
import { InterceptorErrorComponent } from '../components/interceptor-error/interceptor-error.component';

@Injectable()
export class LoaderInterceptor implements HttpInterceptor {
  constructor(
    private loaderService: LoaderService,
    private modalService: NgbModal,
    private toastsService: ToastsService,
    private upUtilityService: UPUtilityService,
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (this._isI18nRequest(request.url)) {
      return next.handle(request);
    }

    this.toastsService.clear();

    const jwtToken = this._getJwtToken();
    const requestWithHeaders = this._cloneRequestWithHeaders(request, jwtToken);
    const shouldShowLoader = this._shouldShowLoader(requestWithHeaders.url);

    if (shouldShowLoader) {
      this.loaderService.show();
    }

    return next.handle(requestWithHeaders).pipe(
      tap((event) => this.onInterceptTap(event)),
      catchError((error: HttpErrorResponse) => this._handleHttpError(error)),
      finalize(() => {
        if (shouldShowLoader) {
          this.loaderService.hide();
        }
      }),
    );
  }

  private _isI18nRequest(url: string): boolean {
    return url.includes('assets/i18n') || url.includes('assets/quid-i18n');
  }

  private _shouldShowLoader(url: string): boolean {
    return !url.includes('ebook/ai/config');
  }

  private _getJwtToken(): string {
    try {
      const token = this.upUtilityService.getItemFromSessionStorage('JWT');
      return token && token !== '' ? token : '';
    } catch {
      return '';
    }
  }

  private _cloneRequestWithHeaders(
    request: HttpRequest<unknown>,
    jwtToken: string,
  ): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${jwtToken}`,
        'Access-Control-Allow-Origin': '*',
      },
    });
  }

  onInterceptTap(event: HttpEvent<unknown>): void {
    if (!(event instanceof HttpResponse)) {
      return;
    }

    const authorizationHeader = event.headers.get('authorization');
    if (!authorizationHeader) {
      return;
    }

    sessionStorage.setItem('JWT', authorizationHeader.split(' ')[1] || '');
  }

  private _handleHttpError(error: HttpErrorResponse): Observable<never> {
    this.modalService.dismissAll();

    const modalContent = this._getTokenErrorModalContent(error);
    if (modalContent && !this.upUtilityService.getTokenError()) {
      this.upUtilityService.setTokenError();
      const modalRef = this.modalService.open(
        InterceptorErrorComponent,
        MD_MODAL_PARAMS,
      );
      modalRef.componentInstance.modalTitle = modalContent.title;
      modalRef.componentInstance.modalBody = modalContent.body;
    }

    return throwError(() => error);
  }

  private _getTokenErrorModalContent(
    error: HttpErrorResponse,
  ): { title: string; body: string } | null {
    const errorCode = (error.error as { errorCode?: string } | null)?.errorCode;

    switch (errorCode) {
      case 'TOKEN_INVALID':
        return {
          title: 'GENERIC.MODAL.INVALID_TOKEN_HEADER',
          body: 'GENERIC.MODAL.INVALID_TOKEN_MSG',
        };
      case 'TOKEN_EXPIRED':
        return {
          title: 'GENERIC.MODAL.EXPIRED_TOKEN_HEADER',
          body: 'GENERIC.MODAL.EXPIRED_TOKEN_MSG',
        };
      default:
        return null;
    }
  }
}
